import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePPCAuth, PPC_API_URL } from './PPCContext';

const EditRequests = () => {
  const { user } = usePPCAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [dashboardFilter, setDashboardFilter] = useState('all');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${PPC_API_URL}/edit_requests.php?action=list&status=${filter}`;
      if (dashboardFilter !== 'all') {
        url += `&dashboard=${dashboardFilter}`;
      }
      const res = await axios.get(url, getAuthHeader());
      if (res.data.success) {
        setRequests(res.data.requests || []);
      }
    } catch (err) {
      console.error('Failed to fetch requests:', err);
      showToast('Failed to fetch requests', 'error');
    }
    setLoading(false);
  }, [filter, dashboardFilter]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/ppc');
      return;
    }
    fetchRequests();
  }, [isAdmin, navigate, fetchRequests]);

  const handleApprove = async (requestId) => {
    try {
      const res = await axios.put(`${PPC_API_URL}/edit_requests.php?action=approve`, 
        { request_id: requestId }, 
        getAuthHeader()
      );
      if (res.data.success) {
        showToast('Request approved and changes applied!');
        fetchRequests();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to approve request', 'error');
    }
  };

  const handleReject = async (requestId) => {
    const reason = prompt('Enter rejection reason (optional):');
    try {
      const res = await axios.put(`${PPC_API_URL}/edit_requests.php?action=reject`, 
        { request_id: requestId, reject_reason: reason }, 
        getAuthHeader()
      );
      if (res.data.success) {
        showToast('Request rejected');
        fetchRequests();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to reject request', 'error');
    }
  };

  const getDashboardColor = (dashboard) => {
    const colors = {
      bravo: '#06b6d4',
      papa: '#ea580c',
      romeo: '#7c3aed',
      golf: '#059669'
    };
    return colors[dashboard] || '#64748b';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const renderValues = (values, label) => {
    if (!values || typeof values !== 'object') return null;
    return (
      <div className="values-section">
        <strong>{label}:</strong>
        <ul>
          {Object.entries(values).map(([key, val]) => (
            <li key={key}><span className="key">{key}:</span> {val}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="edit-requests-page">
      {toast.show && (
        <div className={`toast ${toast.type}`}>{toast.message}</div>
      )}

      <header className="page-header">
        <button className="back-btn" onClick={() => navigate('/ppc')}>← Back</button>
        <h1>Edit Requests Management</h1>
      </header>

      <div className="filters-bar">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Dashboard:</label>
          <select value={dashboardFilter} onChange={(e) => setDashboardFilter(e.target.value)}>
            <option value="all">All Dashboards</option>
            <option value="bravo">BRAVO</option>
            <option value="papa">PAPA</option>
            <option value="romeo">ROMEO</option>
            <option value="golf">GOLF</option>
          </select>
        </div>
        <button className="refresh-btn" onClick={fetchRequests}>🔄 Refresh</button>
      </div>

      <div className="requests-container">
        {loading ? (
          <div className="loading">Loading requests...</div>
        ) : requests.length === 0 ? (
          <div className="no-requests">
            <span className="icon">📋</span>
            <h3>No {filter} requests</h3>
            <p>There are no {filter} edit requests at the moment.</p>
          </div>
        ) : (
          <div className="requests-list">
            {requests.map((req) => (
              <div key={req.id} className="request-card">
                <div className="request-header">
                  <span 
                    className="dashboard-badge" 
                    style={{ background: getDashboardColor(req.dashboard) }}
                  >
                    {req.dashboard?.toUpperCase()}
                  </span>
                  <span className={`status-badge ${req.status}`}>
                    {req.status.toUpperCase()}
                  </span>
                </div>

                <div className="request-body">
                  <div className="request-info">
                    <div className="info-row">
                      <span className="label">Requested by:</span>
                      <span className="value">{req.requested_by_name || req.requested_by_username}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Contractor:</span>
                      <span className="value">{req.contractor}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Entry Date:</span>
                      <span className="value">{req.entry_date}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Shift:</span>
                      <span className="value">{req.shift}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Request Type:</span>
                      <span className="value type-badge">{req.request_type}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Submitted:</span>
                      <span className="value">{formatDate(req.created_at)}</span>
                    </div>
                    {req.reason && (
                      <div className="info-row">
                        <span className="label">Reason:</span>
                        <span className="value">{req.reason}</span>
                      </div>
                    )}
                  </div>

                  <div className="changes-section">
                    {renderValues(req.old_values, 'Current Values')}
                    {renderValues(req.new_values, 'Requested Changes')}
                  </div>
                </div>

                {req.status === 'pending' && (
                  <div className="request-actions">
                    <button 
                      className="approve-btn" 
                      onClick={() => handleApprove(req.id)}
                    >
                      ✓ Approve
                    </button>
                    <button 
                      className="reject-btn" 
                      onClick={() => handleReject(req.id)}
                    >
                      ✕ Reject
                    </button>
                  </div>
                )}

                {req.status !== 'pending' && req.reviewed_at && (
                  <div className="reviewed-info">
                    Reviewed on {formatDate(req.reviewed_at)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .edit-requests-page {
          min-height: 100vh;
          background: #f0f2f5;
          padding: 0;
        }

        .toast {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 12px 24px;
          border-radius: 8px;
          color: white;
          font-weight: 500;
          z-index: 9999;
          animation: slideIn 0.3s ease-out;
        }

        .toast.success { background: linear-gradient(135deg, #2e7d32, #388e3c); }
        .toast.error { background: linear-gradient(135deg, #c62828, #e53935); }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .page-header {
          background: linear-gradient(135deg, #d32f2f 0%, #1565c0 100%);
          padding: 16px 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 2px 14px rgba(0,0,0,.2);
        }

        .back-btn {
          background: rgba(255,255,255,.16);
          border: 1px solid rgba(255,255,255,.28);
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          color: white;
          transition: all 0.2s;
        }

        .back-btn:hover {
          background: rgba(255,255,255,.26);
        }

        .page-header h1 {
          margin: 0;
          font-size: 20px;
          color: white;
        }

        .filters-bar {
          display: flex;
          gap: 16px;
          padding: 16px 24px;
          background: white;
          border-bottom: 1px solid #e2e8f0;
          align-items: center;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .filter-group label {
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
        }

        .filter-group select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 13px;
          background: white;
        }

        .refresh-btn {
          background: linear-gradient(135deg, #1565c0, #1976d2);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          margin-left: auto;
        }

        .requests-container {
          padding: 24px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .loading, .no-requests {
          text-align: center;
          padding: 48px;
          background: white;
          border-radius: 12px;
          color: #6b7a8f;
        }

        .no-requests .icon {
          font-size: 48px;
          display: block;
          margin-bottom: 16px;
        }

        .no-requests h3 {
          margin: 0 0 8px;
          color: #1a2332;
        }

        .requests-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .request-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,.06);
          overflow: hidden;
        }

        .request-header {
          display: flex;
          justify-content: space-between;
          padding: 16px;
          background: #f8f9fb;
          border-bottom: 1px solid #e8ecf0;
        }

        .dashboard-badge {
          color: white;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 700;
        }

        .status-badge {
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
        }

        .status-badge.pending { background: #fff8e1; color: #f9a825; }
        .status-badge.approved { background: #e8f5e9; color: #2e7d32; }
        .status-badge.rejected { background: #ffebee; color: #c62828; }

        .request-body {
          padding: 16px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .request-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .info-row {
          display: flex;
          gap: 8px;
          font-size: 13px;
        }

        .info-row .label {
          color: #6b7a8f;
          min-width: 100px;
        }

        .info-row .value {
          color: #1a2332;
          font-weight: 500;
        }

        .type-badge {
          background: #e3f2fd;
          color: #1565c0;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          text-transform: uppercase;
        }

        .changes-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .values-section {
          background: #f8fafc;
          padding: 12px;
          border-radius: 8px;
          font-size: 13px;
        }

        .values-section strong {
          display: block;
          margin-bottom: 8px;
          color: #1e293b;
        }

        .values-section ul {
          margin: 0;
          padding-left: 16px;
        }

        .values-section li {
          color: #64748b;
          margin-bottom: 4px;
        }

        .values-section .key {
          color: #94a3b8;
        }

        .request-actions {
          display: flex;
          gap: 12px;
          padding: 16px;
          border-top: 1px solid #e2e8f0;
          background: #fafafa;
        }

        .approve-btn, .reject-btn {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .approve-btn {
          background: linear-gradient(135deg, #2e7d32, #388e3c);
          color: white;
        }

        .reject-btn {
          background: linear-gradient(135deg, #c62828, #e53935);
          color: white;
        }

        .approve-btn:hover { transform: translateY(-2px); }
        .reject-btn:hover { transform: translateY(-2px); }

        .reviewed-info {
          padding: 12px 16px;
          border-top: 1px solid #e2e8f0;
          font-size: 12px;
          color: #94a3b8;
          text-align: center;
        }

        @media (max-width: 768px) {
          .request-body {
            grid-template-columns: 1fr;
          }

          .filters-bar {
            flex-direction: column;
            align-items: stretch;
          }

          .refresh-btn {
            margin-left: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default EditRequests;